import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ShoppingCart, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/books-catalog/${slug}`)
      .then((r) => setBook(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1B4B5A]"></div>
        </div>
      </Layout>
    );
  }

  if (notFound || !book) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-4">
          <p className="text-xl text-[#4A5568]">This book could not be found.</p>
          <button onClick={() => navigate('/books')} className="bg-[#1B4B5A] text-white px-6 py-3 rounded-lg font-bold">
            Browse all books
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#FDF6EC] to-[#F5E9D3] py-12">
        <div className="max-w-5xl mx-auto px-4">
          <Link to="/books" className="inline-flex items-center gap-2 text-[#1B4B5A] hover:text-[#B5813B] font-semibold mb-8" data-testid="book-back-link">
            <ArrowLeft className="w-5 h-5" /> All Books
          </Link>

          <div className="grid md:grid-cols-2 gap-10 bg-white rounded-2xl shadow-xl p-8 md:p-12" data-testid="book-detail">
            {/* Cover */}
            <div className="flex justify-center">
              <div className="w-full max-w-xs aspect-[3/4] rounded-xl overflow-hidden shadow-2xl border-4 border-white">
                {book.cover_image_url ? (
                  <img src={book.cover_image_url} alt={book.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#1B4B5A] text-white">
                    <BookOpen className="w-12 h-12 opacity-50" />
                  </div>
                )}
              </div>
            </div>

            {/* Details */}
            <div className="flex flex-col">
              <h1 className="text-4xl md:text-5xl font-bold text-[#1B4B5A] leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                {book.title}
              </h1>
              {book.subtitle && (
                <p className="text-xl text-[#B5813B] mt-2" style={{ fontFamily: 'Georgia, serif' }}>{book.subtitle}</p>
              )}
              {book.author && (
                <p className="text-lg text-[#4A5568] mt-4">by <span className="font-semibold">{book.author}</span></p>
              )}
              {book.description && (
                <p className="text-[#0D1117] leading-relaxed mt-6 whitespace-pre-line">{book.description}</p>
              )}

              <div className="mt-8">
                {book.lulu_url ? (
                  <a
                    href={book.lulu_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="book-buy-button"
                    className="inline-flex items-center gap-2 bg-[#B5813B] text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#9c6f31] transition-all shadow-lg"
                  >
                    <ShoppingCart className="w-5 h-5" /> Buy on Lulu
                  </a>
                ) : (
                  <span className="text-[#4A5568] italic">Purchase link coming soon.</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
